import clsx from 'clsx';

interface ScaleProps {
  /** Valor seleccionado, 1..N */
  value?: number;
  /** Cantidad de pasos (típicamente 4 ó 5) */
  steps: number;
  /**
   * Si se pasa, cada paso muestra su etiqueta debajo del número
   * (más explicativo que sólo etiquetas en los extremos).
   */
  stepLabels?: string[];
  /** Etiqueta del extremo izquierdo (sólo se usa si no hay stepLabels) */
  leftLabel?: string;
  /** Etiqueta del extremo derecho (sólo se usa si no hay stepLabels) */
  rightLabel?: string;
  onChange: (v: number) => void;
}

/**
 * Escala 1..N con botones grandes y etiqueta por paso.
 * Para preguntas tipo "intensidad", "estela", "intensidad habitual".
 */
export default function Scale({
  value,
  steps,
  stepLabels,
  leftLabel,
  rightLabel,
  onChange,
}: ScaleProps) {
  const hasSelection = value != null;
  const useStepLabels = stepLabels && stepLabels.length === steps;

  return (
    <div className="space-y-3 px-2 pt-1">
      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${steps}, 1fr)` }}>
        {Array.from({ length: steps }, (_, i) => i + 1).map((n) => {
          const active = value === n;
          const stepLabel = useStepLabels ? stepLabels![n - 1] : undefined;
          return (
            <div key={n} className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => onChange(n)}
                className={clsx(
                  'w-full rounded-xl2 transition-all py-5 display text-2xl relative',
                  'focus:outline-none',
                  active
                    ? 'bg-gradient-to-b from-elixir-gold-soft to-elixir-gold-deep text-elixir-black ring-2 ring-elixir-gold ring-offset-2 ring-offset-elixir-black shadow-[0_8px_24px_-10px_rgba(201,169,97,0.45)]'
                    : hasSelection
                    ? 'border border-elixir-gold/15 bg-elixir-ink/50 text-elixir-cream/55 opacity-60 hover:opacity-95'
                    : 'border border-elixir-gold/20 bg-elixir-ink/60 text-elixir-cream/85 hover:border-elixir-gold/55'
                )}
              >
                {n}
                {active && (
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-elixir-gold text-elixir-black flex items-center justify-center shadow ring-2 ring-elixir-black">
                    <svg width="12" height="12" viewBox="0 0 18 18" fill="none">
                      <path
                        d="M3.5 9.5 L7 13 L14.5 5"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                )}
              </button>
              {stepLabel && (
                <span
                  className={clsx(
                    'text-[10px] tracking-[0.18em] uppercase text-center leading-tight',
                    active ? 'text-elixir-gold' : 'text-elixir-cream/55'
                  )}
                >
                  {stepLabel}
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Si no hay etiquetas por paso, mantenemos los extremos */}
      {!useStepLabels && (leftLabel || rightLabel) && (
        <div className="flex justify-between text-xs text-elixir-cream/55 px-1">
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
    </div>
  );
}
